import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Rating,
  Button,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Snackbar,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import StarIcon from '@mui/icons-material/Star';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import { Favorite, FavoriteBorder, Share } from '@mui/icons-material';
import SearchBar from '../components/SearchBar';
import MapView from '../components/MapView';
import EventLightbox from '../components/EventLightbox';
import { searchAPI, eventAPI } from '../services/api';
import { SearchResponse, Event } from '../types';
import { useFavorites } from '../contexts/FavoritesContext';
import { updateSEOTags, generateSEOContent } from '../utils/seoUtils';

const SearchResultsYelp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const locationQuery = searchParams.get('location');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [searchData, setSearchData] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [lightboxEvent, setLightboxEvent] = useState<Event | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'hotel' | 'event' | 'activities' | 'vacation_rentals'>('all');
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [topVisibleEvent, setTopVisibleEvent] = useState<Event | null>(null);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!locationQuery) return;

      setLoading(true);
      setError(null);
      try {
        const data = await searchAPI.search(locationQuery);
        console.log('Search data received:', data);
        console.log('Location object:', data.location);
        console.log('Location coordinates:', {
          latitude: data.location?.latitude,
          longitude: data.location?.longitude,
          hasCoords: !!(data.location?.latitude && data.location?.longitude)
        });
        setSearchData(data);
      } catch (error: any) {
        console.error('Error fetching search results:', error);
        if (error.response?.status === 404) {
          setError('Location not found. Please try a different search.');
        } else {
          setError('An error occurred while searching. Please try again.');
        }
        setSearchData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [locationQuery]);

  // Filter events (moved up to be available for scroll detection)
  const filteredEvents = searchData?.events.filter(event => {
    // Filter by category
    const categoryMatch = categoryFilter === 'all' || event.category === categoryFilter;
    
    // Filter by selected features (if any are selected)
    const featureMatch = selectedFeatures.length === 0 || 
      selectedFeatures.some(featureId => 
        searchData?.features.find(f => f._id === featureId)?.eventIds.includes(event.uniqueId)
      );
    
    return categoryMatch && featureMatch;
  }) || [];

  // Scroll detection for desktop to highlight top visible event
  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      const scrollContainer = document.querySelector('[data-scroll-container]');
      if (!scrollContainer || filteredEvents.length === 0) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const containerTop = containerRect.top;

      // Find the first event card that's visible or partially visible
      const eventCards = scrollContainer.querySelectorAll('[data-event-id]');
      let topEvent: Event | null = null;

      for (let i = 0; i < eventCards.length; i++) {
        const card = eventCards[i];
        const cardRect = card.getBoundingClientRect();
        const cardTop = cardRect.top;
        const cardBottom = cardRect.bottom;

        // Check if card is visible (top edge is past container top but bottom is still visible)
        if (cardBottom > containerTop && cardTop <= containerTop + 100) {
          const eventId = card.getAttribute('data-event-id');
          topEvent = filteredEvents.find(e => e.uniqueId === eventId) || null;
          break;
        }
      }

      setTopVisibleEvent(topEvent);
    };

    const scrollContainer = document.querySelector('[data-scroll-container]');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial call

      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [isMobile, filteredEvents]);

  // Update SEO tags when search data changes
  useEffect(() => {
    if (searchData?.location && filteredEvents.length > 0) {
      // Collect all SEO keywords from features
      const allKeywords = searchData.features.reduce((keywords: string[], feature) => {
        if (feature.seoKeywords) {
          keywords.push(...feature.seoKeywords);
        }
        return keywords;
      }, []);

      // Remove duplicates and limit to most relevant
      const uniqueKeywords = Array.from(new Set(allKeywords)).slice(0, 10);

      // Generate SEO content
      const { title, description } = generateSEOContent(
        searchData.location.name,
        filteredEvents.length,
        uniqueKeywords
      );

      // Update SEO tags
      updateSEOTags({
        title,
        description,
        keywords: uniqueKeywords,
        locationName: searchData.location.name
      });
    }
  }, [searchData, filteredEvents]);

  // Auto-scroll to listings on mobile when data loads (for shared links)
  useEffect(() => {
    if (isMobile && searchData && filteredEvents.length > 0 && !loading) {
      // Small delay to ensure layout is rendered
      setTimeout(() => {
        const viewportHeight = window.innerHeight;
        const scrollTarget = viewportHeight * 0.6; // Scroll to show listings overlay
        window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
      }, 100);
    }
  }, [isMobile, searchData, filteredEvents.length, loading]);

  const handleEventClick = async (event: Event) => {
    try {
      await eventAPI.incrementClick(event.uniqueId);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error tracking click:', error);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleImageClick = (event: Event, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMobile) return; // Don't open lightbox on mobile
    setLightboxEvent(event);
    setLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setLightboxOpen(false);
    setLightboxEvent(null);
  };

  const handleViewMore = (event: Event) => {
    setLightboxEvent(event);
    setLightboxOpen(true);
  };

  // Helper function to get features for an event
  const getFeaturesForEvent = (eventId: string) => {
    return searchData?.features.filter(feature => 
      feature.eventIds.includes(eventId)
    ) || [];
  };

  const EventCard = ({ event }: { event: Event }) => {
    const eventFeatures = getFeaturesForEvent(event.uniqueId);
    const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
    const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
    const [saveSnackbarOpen, setSaveSnackbarOpen] = useState(false);
    const isEventFavorite = isFavorite(event.uniqueId);

    const handleFavoriteClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isEventFavorite) {
        removeFromFavorites(event.uniqueId);
      } else {
        addToFavorites(event);
        setSaveSnackbarOpen(true);
      }
    };

    const handleShare = async (e: React.MouseEvent) => {
      e.stopPropagation();
      
      const shareData = {
        title: event.eventTitle,
        text: event.subhead || `Experience ${event.eventTitle}`,
        url: event.affiliateUrl,
      };

      // Check if device supports native sharing
      if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        try {
          await navigator.share(shareData);
        } catch (error) {
          console.error('Error sharing:', error);
        }
      } else {
        // Desktop: Copy to clipboard
        try {
          await navigator.clipboard.writeText(event.affiliateUrl);
          setShareSnackbarOpen(true);
        } catch (error) {
          console.error('Error copying to clipboard:', error);
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = event.affiliateUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          setShareSnackbarOpen(true);
        }
      }
    };
    
    return (
      <Card 
        data-event-id={event.uniqueId}
        sx={{ 
          mb: 2, 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
          }
        }}
        onClick={() => setSelectedEvent(event)}
      >
        <CardMedia
          component="img"
          sx={{ 
            width: { xs: '100%', sm: 200 }, 
            height: { xs: 200, sm: 'auto' },
            objectFit: 'cover',
            cursor: 'pointer'
          }}
          image={event.imageUrl}
          alt={event.eventTitle}
          onClick={(e) => handleImageClick(event, e)}
        />
        <CardContent sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
                {event.eventTitle}
              </Typography>
              
              {event.price && (
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: '#059669',
                    fontWeight: 700,
                    fontSize: '1rem',
                    mb: 1,
                  }}
                >
                  {event.price}
                </Typography>
              )}
              
              {/* Feature Tags */}
              {eventFeatures.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {eventFeatures.map((feature) => (
                    <Chip
                      key={feature._id}
                      label={feature.title}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1',
                        fontWeight: 500,
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        fontSize: '0.75rem',
                        height: '20px',
                      }}
                    />
                  ))}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Rating value={event.starRating} readOnly size="small" />
                <Typography variant="body2" color="text.secondary">
                  ({event.starRating})
                </Typography>
                {event.category && (
                  <Chip 
                    label={
                      event.category === 'hotel' ? 'Hotel' : 
                      event.category === 'vacation_rentals' ? 'Vacation Rental' :
                      event.category === 'activities' ? 'Activities' : 
                      event.category === 'event' ? 'Event' :
                      event.category
                    }
                    size="small" 
                    icon={
                      event.category === 'hotel' || event.category === 'vacation_rentals' ? <HomeIcon /> : 
                      event.category === 'activities' ? <LocalActivityIcon /> : 
                      <StarIcon />
                    }
                    color={
                      event.category === 'hotel' || event.category === 'vacation_rentals' ? 'primary' : 
                      event.category === 'activities' ? 'info' : 
                      'secondary'
                    }
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {event.subhead}
          </Typography>
          
          {event.address && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
              <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {event.address.street && `${event.address.street}, `}
                {event.address.city} {event.address.state} {event.address.zip}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant={isEventFavorite ? 'contained' : 'outlined'}
              size="small"
              onClick={handleFavoriteClick}
              startIcon={isEventFavorite ? <Favorite /> : <FavoriteBorder />}
              sx={{
                background: isEventFavorite ? '#ef4444' : 'transparent',
                color: isEventFavorite ? 'white' : '#ef4444',
                borderColor: '#ef4444',
                minWidth: 'auto',
                '&:hover': {
                  background: isEventFavorite ? '#dc2626' : 'rgba(239, 68, 68, 0.1)',
                  borderColor: '#dc2626',
                },
                fontSize: '0.75rem',
                px: 1,
                order: { xs: 1, sm: 2 },
              }}
            >
              {isEventFavorite ? 'Saved' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleShare}
              startIcon={<Share />}
              sx={{
                color: '#6366f1',
                borderColor: '#6366f1',
                minWidth: 'auto',
                '&:hover': {
                  background: 'rgba(99, 102, 241, 0.1)',
                  borderColor: '#4f46e5',
                },
                fontSize: '0.75rem',
                px: 1,
                order: { xs: 2, sm: 3 },
              }}
            >
              Share
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEventClick(event);
              }}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
                fontSize: '0.75rem',
                flex: { xs: '1 1 100%', sm: 1 },
                order: { xs: 3, sm: 1 },
                whiteSpace: 'nowrap',
              }}
            >
              Book Now ⇗
            </Button>
          </Box>
        </CardContent>
        
        <Snackbar
          open={shareSnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setShareSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setShareSnackbarOpen(false)} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            Link copied to clipboard!
          </Alert>
        </Snackbar>
        
        <Snackbar
          open={saveSnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSaveSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSaveSnackbarOpen(false)} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            Saved to favorites!
          </Alert>
        </Snackbar>
      </Card>
    );
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar 
        position="sticky" 
        elevation={1} 
        sx={{ 
          bgcolor: 'white', 
          color: 'text.primary',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          zIndex: 1200,
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 600 } }}>
            <SearchBar variant="standard" />
          </Box>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as any)}
              label="Category"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="hotel">Hotels</MenuItem>
              <MenuItem value="event">Events</MenuItem>
              <MenuItem value="activities">Activities</MenuItem>
              <MenuItem value="vacation_rentals">Vacation Rentals</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        position: 'relative', 
        overflow: isMobile ? 'visible' : 'hidden' 
      }}>
        {/* Desktop Layout */}
        {!isMobile && (
          <>
            {/* Results List - Desktop */}
            <Box 
              data-scroll-container
              sx={{ 
                width: '50%',
                height: '100%',
                overflowY: 'auto',
                p: 3,
              }}
            >
              {locationQuery && searchData?.location && (
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  {searchData.location.name} - {filteredEvents.length} results
                </Typography>
              )}
              
              {/* Feature Filter Chips */}
              {searchData?.features && searchData.features.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                    Featured Collections
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {searchData.features.map((feature) => (
                      <Chip
                        key={feature._id}
                        label={feature.title}
                        variant={selectedFeatures.includes(feature._id!) ? 'filled' : 'outlined'}
                        onClick={() => {
                          setSelectedFeatures(prev => 
                            prev.includes(feature._id!)
                              ? prev.filter(id => id !== feature._id)
                              : [...prev, feature._id!]
                          );
                        }}
                        sx={{
                          borderRadius: '16px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          ...(selectedFeatures.includes(feature._id!) ? {
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            }
                          } : {
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.50',
                              borderColor: 'primary.dark',
                            }
                          })
                        }}
                      />
                    ))}
                    {selectedFeatures.length > 0 && (
                      <Chip
                        label="Clear All"
                        variant="outlined"
                        onClick={() => setSelectedFeatures([])}
                        sx={{
                          borderRadius: '16px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          borderColor: 'grey.400',
                          color: 'text.secondary',
                          '&:hover': {
                            bgcolor: 'grey.100',
                            borderColor: 'grey.600',
                          }
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )}
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : filteredEvents.length > 0 ? (
                <Box>
                  {filteredEvents.map((event) => (
                    <EventCard key={event.uniqueId} event={event} />
                  ))}
                </Box>
              ) : (
                <Alert severity="info">
                  No {categoryFilter === 'all' ? 'activities' : categoryFilter + 's'} found for this location.
                </Alert>
              )}
            </Box>

            {/* Map - Desktop */}
            <Box
              sx={{
                position: 'relative',
                width: '50%',
                height: '100%',
                borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <MapView 
                events={filteredEvents}
                selectedEvent={selectedEvent}
                onMarkerClick={setSelectedEvent}
                locationName={searchData?.location?.name}
                location={searchData?.location}
                onViewMore={handleViewMore}
                highlightedEvent={topVisibleEvent}
              />
            </Box>
          </>
        )}

        {/* Mobile Layout */}
        {isMobile && (
          <>
            {/* Fixed Map Background - Mobile */}
            <Box
              sx={{
                position: 'fixed',
                top: '64px', // Height of AppBar
                left: 0,
                right: 0,
                height: '80vh',
                zIndex: 0,
              }}
            >
              <MapView 
                events={filteredEvents}
                selectedEvent={selectedEvent}
                onMarkerClick={setSelectedEvent}
                locationName={searchData?.location?.name}
                location={searchData?.location}
                onViewMore={handleViewMore}
                highlightedEvent={topVisibleEvent}
              />
            </Box>

            {/* Scrollable Results Overlay - Mobile */}
            <Box 
              sx={{ 
                position: 'absolute',
                top: '75vh', // Start overlay only where content begins
                left: 0,
                right: 0,
                minHeight: '25vh', // Cover remaining viewport height
                zIndex: 100,
              }}
            >
              {/* Pull tab / Handle */}
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'white',
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                  p: 1,
                  boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  zIndex: 101,
                }}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 4,
                    bgcolor: 'grey.400',
                    borderRadius: 2,
                    mx: 'auto',
                  }}
                />
                {locationQuery && searchData?.location && (
                  <Typography variant="subtitle1" sx={{ textAlign: 'center', mt: 1, fontWeight: 600 }}>
                    {filteredEvents.length} results in {searchData.location.name}
                  </Typography>
                )}
              </Box>

              {/* Results Content */}
              <Box
                sx={{
                  bgcolor: 'white',
                  minHeight: 'calc(100vh - 64px)',
                  p: 2,
                }}
              >
                {/* Feature Filter Chips - Mobile */}
                {searchData?.features && searchData.features.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Featured Collections
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {searchData.features.map((feature) => (
                        <Chip
                          key={feature._id}
                          label={feature.title}
                          variant={selectedFeatures.includes(feature._id!) ? 'filled' : 'outlined'}
                          onClick={() => {
                            setSelectedFeatures(prev => 
                              prev.includes(feature._id!)
                                ? prev.filter(id => id !== feature._id)
                                : [...prev, feature._id!]
                            );
                          }}
                          sx={{
                            borderRadius: '16px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            ...(selectedFeatures.includes(feature._id!) ? {
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              }
                            } : {
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': {
                                bgcolor: 'primary.50',
                                borderColor: 'primary.dark',
                              }
                            })
                          }}
                        />
                      ))}
                      {selectedFeatures.length > 0 && (
                        <Chip
                          label="Clear All"
                          variant="outlined"
                          onClick={() => setSelectedFeatures([])}
                          sx={{
                            borderRadius: '16px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            borderColor: 'grey.400',
                            color: 'text.secondary',
                            '&:hover': {
                              bgcolor: 'grey.100',
                              borderColor: 'grey.600',
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
                
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : filteredEvents.length > 0 ? (
                  <Box>
                    {filteredEvents.map((event) => (
                      <EventCard key={event.uniqueId} event={event} />
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info">
                    No {categoryFilter === 'all' ? 'activities' : categoryFilter + 's'} found for this location.
                  </Alert>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
      
      <EventLightbox
        event={lightboxEvent}
        open={lightboxOpen}
        onClose={handleLightboxClose}
      />
    </Box>
  );
};

export default SearchResultsYelp;